import { ColumnDef, Row, RowModel, Table } from "@tanstack/react-table";
import React, { Dispatch, SetStateAction, useCallback, useMemo } from "react";
import { Button } from "../../button";
import { ArrowUpDown } from "lucide-react";
import TableWrapper from "../TableWrapper";
import { Device } from "../../../hooks/common/interface";
import { useTranslation } from "react-i18next";

interface FRDevicesFRConfigTableProps {
  frDevices: Device[];
  page: number;
  pages: number;
  setPage: Dispatch<SetStateAction<number>>;
  search: string;
  setSearch: Dispatch<SetStateAction<string>>;
  count: number;
  setSelectedDeviceIPAddresses: Dispatch<SetStateAction<string[]>>;
  pageSize: number;
  setPageSize: Dispatch<SetStateAction<number>>;
}

const FRDevicesFRConfigTable = ({
  frDevices,
  pages,
  page,
  setPage,
  search,
  setSearch,
  count,
  setSelectedDeviceIPAddresses,
  pageSize,
  setPageSize,
}: FRDevicesFRConfigTableProps) => {
  const { t, i18n } = useTranslation();

  const onRowSelection = useCallback(
    (
      selectedRowsModel: RowModel<{
        [key: string]: any;
      }>
    ) => {
      setSelectedDeviceIPAddresses(Object.keys(selectedRowsModel?.rowsById));
    },
    [setSelectedDeviceIPAddresses]
  );

  const columns: ColumnDef<Device>[] = useMemo(
    () => [
      {
        accessorKey: "select",
        header: ({ table }: { table: Table<Device> }) => (
          <div className="px-5">
            <input
              type="checkbox"
              checked={table.getIsAllRowsSelected()}
              onChange={table.getToggleAllRowsSelectedHandler()}
              className="cursor-pointer"
            />
          </div>
        ),
        cell: ({ row }: { row: Row<Device> }) => (
          <input
            type="checkbox"
            checked={row.getIsSelected()}
            onChange={row.getToggleSelectedHandler()}
            className="cursor-pointer"
          />
        ),
      },
      {
        accessorKey: "s_DeviceName_En",
        header: ({ column }) => (
          <Button
            type="button"
            className="flex w-full justify-between text-md"
            variant="ghost"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          >
            {t("device")}
            <ArrowUpDown className="ml-2 h-4 w-4" />
          </Button>
        ),
        cell: ({ row }) => {
          return (
            <span className="capitalize">
              {i18n.language === "en"
                ? row.original.s_DeviceName_En
                : row.original.s_DeviceName_Ar}
            </span>
          );
        },
      },
      {
        accessorKey: "s_IPAddress",
        header: ({ column }) => (
          <Button
            type="button"
            className="flex w-full justify-between text-md"
            variant="ghost"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          >
            {t("ipAddress")}
            <ArrowUpDown className="ml-2 h-4 w-4" />
          </Button>
        ),
        cell: ({ row }) => {
          return <span>{row.original.s_IPAddress}</span>;
        },
      },
      {
        accessorKey: "s_LocationName_En",
        header: ({ column }) => (
          <Button
            type="button"
            className="flex w-full justify-between text-md"
            variant="ghost"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          >
            {t("location")}
            <ArrowUpDown className="ml-2 h-4 w-4" />
          </Button>
        ),
        cell: ({ row }) => {
          return (
            <span>
              {i18n.language === "en"
                ? row.original.s_LocationName_En
                : row.original.s_LocationName_Ar}
            </span>
          );
        },
      },
      {
        accessorKey: "s_BuildingName_En",
        header: ({ column }) => (
          <Button
            type="button"
            className="flex w-full justify-between text-md"
            variant="ghost"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          >
            {t("building")}
            <ArrowUpDown className="ml-2 h-4 w-4" />
          </Button>
        ),
        cell: ({ row }) => {
          return (
            <span>
              {i18n.language === "en"
                ? row.original.s_BuildingName_En
                : row.original.s_BuildingName_Ar}
            </span>
          );
        },
      },
    ],
    [i18n.language, t]
  );

  return (
    <TableWrapper
      data={frDevices}
      columns={columns}
      search={search}
      setSearch={setSearch}
      page={page}
      setPage={setPage}
      totalPages={pages}
      count={count}
      isSearch
      rowSelectionId="s_IPAddress"
      onRowSelection={onRowSelection}
      pageSize={pageSize}
      setPageSize={setPageSize}
      isDarkModeStyles={false}
    />
  );
};

export default React.memo(FRDevicesFRConfigTable);
